import {
  HttpErrorResponse,
  type HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HotToastService } from '@ngxpert/hot-toast';
import { throwError } from 'rxjs';
import { ConnectivityService } from './connectivity.service';

/** Interceptor that short-circuits mutating requests when the device is offline.
 *
 * Shows a user-friendly toast so failures aren't silent. GETs still pass through
 * so the cache interceptor can serve stale data.
 *
 * Must sit BEFORE the cache interceptor in the chain to avoid running UNCACHE
 * logic for mutations that never left the device.
 */
export const offlineInterceptor: HttpInterceptorFn = (request, next) => {
  if (request.method === 'GET') {
    return next(request);
  }

  const connectivity = inject(ConnectivityService);
  if (connectivity.isOnline()) {
    return next(request);
  }

  const toast = inject(HotToastService);
  const translate = inject(TranslateService);

  toast.error(translate.instant('connectivity.mutation-offline'), {
    duration: 4000,
    id: 'offline-mutation',
  });

  return throwError(
    () =>
      new HttpErrorResponse({
        status: 0,
        statusText: 'Offline',
        url: request.url,
        error: { code: 'offline', message: 'Device is offline' },
      }),
  );
};
